import { Button } from '@/components/ui/button';
import { getMonthNameByDateObject, getWeekdayByDate } from '@/utils';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useRouteParams } from './useRouteParams';
import { useDialogContext } from './DialogContext';
import { useNavigate } from 'react-router-dom';
import { useFormSubmit } from './useFormSubmit';
import { useTranslation } from 'react-i18next';

dayjs.extend(isoWeek);

// eslint-disable-next-line max-lines-per-function
export function Header() {
  const { searchParams, routeParams } = useRouteParams();
  const { datum } = searchParams;

  const { dispatch, planwerteForm } = useDialogContext();
  const navigate = useNavigate();
  const { t } = useTranslation();

  function getIsPrevDateButtonDisabled() {
    const prevDate = dayjs(datum).subtract(1, 'day');
    return prevDate.get('month') !== dayjs(datum).get('month');
  }

  function getIsNextButtonDisabled() {
    const nextDate = dayjs(datum).add(1, 'day');
    return nextDate.get('month') !== dayjs(datum).get('month');
  }

  function resetAndNavigateToNextDay() {
    const nextDay = dayjs(datum).add(1, 'day').format('YYYY-MM-DD');
    dispatch({ type: 'reset' });
    dispatch({
      type: 'updateDialogMessage',
      dialogMessage: {
        message: t('domain.controlling.umsatzplanung.planung.success'),
        type: 'success',
      },
    });
    navigate(
      `/controlling/umsatzplanung/${routeParams.jahr}/${routeParams.monat}/planwerte/edit?datum=${nextDay}`,
    );
  }

  function resetAndNavigateToPrevDay() {
    const prevDay = dayjs(datum).subtract(1, 'day').format('YYYY-MM-DD');
    dispatch({ type: 'reset' });
    dispatch({
      type: 'updateDialogMessage',
      dialogMessage: {
        message: t('domain.controlling.umsatzplanung.planung.success'),
        type: 'success',
      },
    });
    navigate(
      `/controlling/umsatzplanung/${routeParams.jahr}/${routeParams.monat}/planwerte/edit?datum=${prevDay}`,
    );
  }

  function onError(message?: string) {
    dispatch({
      type: 'updateDialogMessage',
      dialogMessage: {
        message:
          message ??
          t('domain.controlling.umsatzplanung.planung.errors.unknownError'),
        type: 'error',
      },
    });
  }

  const { handleSubmit: handleSubmitAndNavigateToPrevDay } = useFormSubmit(
    resetAndNavigateToPrevDay,
    onError,
  );

  const { handleSubmit: handleSubmitAndNavigateToNextDay } = useFormSubmit(
    resetAndNavigateToNextDay,
    onError,
  );

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-2">
        <Button
          onClick={(e) => {
            if (!planwerteForm.isDirty && !planwerteForm.hasChanges) {
              handleSubmitAndNavigateToPrevDay(e);
            } else if (planwerteForm.hasChanges) {
              handleSubmitAndNavigateToPrevDay(e);
            } else {
              resetAndNavigateToPrevDay();
              const prevDay = dayjs(datum)
                .subtract(1, 'day')
                .format('YYYY-MM-DD');
              dispatch({ type: 'reset' });
              navigate(
                `/controlling/umsatzplanung/${routeParams.jahr}/${routeParams.monat}/planwerte/edit?datum=${prevDay}`,
              );
            }
          }}
          size="icon"
          variant="icon"
          className="text-neutrals-N700 disabled:text-neutrals-N500"
          disabled={
            getIsPrevDateButtonDisabled() || planwerteForm.isSubmitting
          }>
          <FontAwesomeIcon className="text-xl" icon={faChevronLeft} />
        </Button>
        <Button
          size="icon"
          variant="icon"
          onClick={(e) => {
            if (!planwerteForm.isDirty && !planwerteForm.hasChanges) {
              handleSubmitAndNavigateToNextDay(e);
            } else if (planwerteForm.hasChanges) {
              handleSubmitAndNavigateToNextDay(e);
            } else {
              const nextDay = dayjs(datum).add(1, 'day').format('YYYY-MM-DD');
              navigate(
                `/controlling/umsatzplanung/${routeParams.jahr}/${routeParams.monat}/planwerte/edit?datum=${nextDay}`,
              );
              dispatch({ type: 'reset' });
            }
          }}
          className="text-neutrals-N700 disabled:text-neutrals-N500"
          disabled={getIsNextButtonDisabled() || planwerteForm.isSubmitting}>
          <FontAwesomeIcon className="text-xl" icon={faChevronRight} />
        </Button>
      </div>
      <div className="font-bold text-neutrals-N900 flex gap-1 items-baseline">
        <span>{getWeekdayByDate(new Date(datum)).name},</span>
        <span>{dayjs(datum).format('D.')}</span>
        <span>{getMonthNameByDateObject(new Date(datum))}</span>
        <span>{dayjs(datum).format('YYYY')}</span>
      </div>
      <div className="text-xs uppercase bg-neutrals-N300 py-[2px] px-1 rounded-full">
        KW {dayjs(datum).isoWeek()}
      </div>
    </div>
  );
}
